<template>
  <div class="flex flex-col items-start sm:flex-row sm:items-center gap-12 mt-8 mb-24">
    <TrustedSitterBannerIcon :width="148" :height="30" aria-hidden="true" />
    <Pill :color="PillColor.brand" class="!text-brand">EXCLUSIVE INVITATION</Pill>
  </div>

  <div class="space-y-16 mb-16">
    <p class="text-body text-grey-body mb-0">
      You’ve been selected for early access to Sittercity’s new Trusted Caregiver Program! It’s designed to recognize and support
      caregivers who continually meet a higher standard for trust, reliability, and professionalism.
    </p>
    <p class="text-body text-grey-body mb-0">
      Trusted Sitters earn a profile badge, which helps you stand out and builds credibility that can open the door to more
      connections and opportunities. To become a Trusted Sitter, you’ll need to complete a few required steps.
    </p>
    <p class="text-strong-body text-grey-body mb-0">Interested? Accept your invitation to get started.</p>
  </div>

  <a
    href="https://family.sittercity.com/trusted-caregiver/"
    class="text-body text-primary inline-flex items-center gap-8 primary-focus mb-16"
    target="_blank"
  >
    Learn more about the Trusted Caregiver Program
    <NewTabIcon :width="16" :height="16" aria-hidden="true" />
  </a>

  <p v-if="errorMessage" class="text-body text-critical mb-16" role="alert">{{ errorMessage }}</p>

  <div class="flex flex-col items-center pt-8">
    <div class="flex justify-center gap-16">
      <Button value="I decline" variant="outlined" size="medium" :disabled="Boolean(submitting)" @click="emit('decline')" />
      <Button
        value="I accept"
        variant="primary"
        size="medium"
        :loading="submitting === 'ACCEPT'"
        :disabled="Boolean(submitting)"
        @click="emit('accept')"
      />
    </div>
    <button
      type="button"
      class="text-body text-grey-body mt-16 primary-focus"
      :disabled="Boolean(submitting)"
      @click="emit('remindLater')"
    >
      Remind me later
    </button>
  </div>

  <hr class="border-grey-divider-subdued mt-24 mb-16" />
  <p class="text-small text-grey-body mb-0">
    By accepting this invitation, you acknowledge and agree that participation in the Trusted Sitter Program requires enrollment
    in Sittercity's
    <a
      href="https://support.sittercity.com/hc/en-us/articles/56944661980315-What-is-Continuous-Criminal-Monitoring?utm_medium=help-center&utm_source=tcp-unbounce&utm_campaign=&utm_content=continuous-criminal-monitoring&utm_term="
      class="text-grey-body underline primary-focus"
      target="_blank"
      rel="noopener noreferrer"
      >Continuous Criminal Monitoring</a
    >
    program as a condition of receiving and maintaining Trusted Sitter status.
  </p>
</template>

<script setup lang="ts">
import Button from '@/components/button/Button.vue';
import Pill from '@/components/pill/Pill.vue';
import { PillColor } from '@/components/pill/Pill.vue';
import TrustedSitterBannerIcon from '@/graphics/icons/TrustedSitterBannerIcon.vue';
import NewTabIcon from '@/graphics/icons/NewTabIcon.vue';
import type { TrustedCaregiverInviteResponse } from '@/graphql/provider/trusted_caregiver/mutations';

defineOptions({ name: 'TrustedCaregiverInviteContent' });

defineProps<{
  submitting: TrustedCaregiverInviteResponse | null;
  errorMessage: string;
}>();

const emit = defineEmits<{
  decline: [];
  accept: [];
  remindLater: [];
}>();
</script>
